import { useMemo, useState, useCallback } from "react";
import { Box, Flex, Text, Button, Avatar } from "@radix-ui/themes";
import { toast } from "sonner";
import {
  ReleaseConversation,
  ReleaseConversationFeedback,
  ReleaseComment,
} from "@/v2/entities/release-conversation/ReleaseConversation.model";
import { useUpdateReleaseConversationFeedback } from "@/v2/entities/release-conversation/ReleaseConversation.api";
import { useSimulator } from "@/v2/features/simulator/context/SimulatorWrapper";
import { SessionMetadataInline } from "@/v2/features/simulator/parts/topicsList/shared/SessionMetadataComponents";
import Dialog from "@/components/shared/dialog/Dialog";
import styles from "./ConversationDetail.module.scss";

interface ConversationDetailProps {
  conversation: ReleaseConversation | null;
  onFeedbackUpdate?: () => void;
  onShowSimilar?: (searchText: string) => void;
}

export function ConversationDetail({
  conversation,
  onFeedbackUpdate,
  onShowSimilar,
}: ConversationDetailProps) {
  const { releasesWithVersions, topics } = useSimulator();
  const { mutate: updateFeedback, isLoading: isUpdating } =
    useUpdateReleaseConversationFeedback();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const hasKnownVersion = useMemo(() => {
    if (!conversation || !releasesWithVersions) return false;
    const release = releasesWithVersions.find(
      (r) => r.id === conversation.releaseId
    );
    return !!release;
  }, [conversation, releasesWithVersions]);

  const newFeedback = useMemo<ReleaseConversationFeedback | null>(() => {
    if (!conversation) return null;
    return conversation.feedback === "POSITIVE" ? "FORGOTTEN" : "POSITIVE";
  }, [conversation]);

  const handleForgetClick = useCallback(() => {
    if (!conversation) return;
    setConfirmDialogOpen(true);
  }, [conversation]);

  const handleForgetConfirm = useCallback(() => {
    if (!conversation || !newFeedback) return;

    updateFeedback(
      {
        id: conversation.id,
        data: { feedback: newFeedback },
      },
      {
        onSuccess: () => {
          toast.success(
            `Conversation marked as ${
              newFeedback === "FORGOTTEN" ? "forgotten" : "positive"
            }`
          );
          onFeedbackUpdate?.();
          setConfirmDialogOpen(false);
        },
        onError: () => {
          toast.error("Failed to update conversation");
          setConfirmDialogOpen(false);
        },
      }
    );
  }, [conversation, newFeedback, updateFeedback, onFeedbackUpdate]);

  const handleForgetCancel = useCallback(() => {
    setConfirmDialogOpen(false);
  }, []);

  const handleShowSimilar = useCallback(() => {
    if (!conversation || !onShowSimilar) return;

    const firstUserComment = conversation.releaseComments?.find(
      (comment: ReleaseComment) => !comment.isAgent
    );

    if (firstUserComment?.comment) {
      onShowSimilar(firstUserComment.comment);
    }
  }, [conversation, onShowSimilar]);

  if (!conversation) {
    return (
      <Flex
        width="100%"
        height="100%"
        direction="column"
        align="center"
        p="5"
        pt="400px"
        gap="3"
      >
        <Text size="4" weight="medium" color="gray">
          No conversation selected
        </Text>
        <Text size="2" color="gray" align="center">
          Select a conversation from the sidebar to view its details
        </Text>
      </Flex>
    );
  }

  const filteredComments = conversation.releaseComments?.filter(
    (comment: ReleaseComment) =>
      !comment.comment.toLowerCase().includes("reset") &&
      !comment.comment.includes("--")
  );

  const formatMessage = (message: string) => {
    if (message === "talk_to_an_agent") {
      return "Quack will escalate it to an agent";
    }
    return message;
  };

  return (
    <Box className={styles.detailContainer}>
      <Flex direction="column" gap="3" p="4">
        <SessionMetadataInline
          session={conversation}
          releasesWithVersions={releasesWithVersions}
          topics={topics}
          showVersion={hasKnownVersion}
          showTopic={true}
          showFeedback={true}
          showPendingReview={true}
          showSubmitter={true}
          showInteractionType={true}
        />

        <Flex justify="end" gap="2">
          <Button
            variant="outline"
            size="2"
            onClick={handleShowSimilar}
            disabled={
              !onShowSimilar ||
              !conversation.releaseComments?.some((c) => !c.isAgent)
            }
          >
            Find Related
          </Button>
          <Button
            variant="outline"
            color={conversation.feedback === "POSITIVE" ? "red" : "green"}
            size="2"
            onClick={handleForgetClick}
            disabled={isUpdating}
          >
            {conversation.feedback === "POSITIVE" ? "Forget" : "Mark Positive"}
          </Button>
        </Flex>

        {!filteredComments || filteredComments.length === 0 ? (
          <Box p="4">
            <Text size="2" color="gray">
              No messages found
            </Text>
          </Box>
        ) : (
          <Box>
            {filteredComments.map((comment: ReleaseComment, index: number) => (
              <Flex
                key={index}
                direction="column"
                className={
                  comment.isAgent
                    ? `${styles.messageWrapper} ${styles.agentMessage}`
                    : `${styles.messageWrapper} ${styles.userMessage}`
                }
                mb="4"
                p="2"
              >
                <Flex
                  align="center"
                  gap="2"
                  className={
                    comment.isAgent
                      ? `${styles.messageHeader} ${styles.agentHeader}`
                      : `${styles.messageHeader} ${styles.userHeader}`
                  }
                >
                  <Avatar
                    size="2"
                    fallback={comment.isAgent ? "Q" : "U"}
                    radius="full"
                    style={{
                      backgroundColor: comment.isAgent
                        ? "var(--amber-3)"
                        : "var(--gray-3)",
                      color: comment.isAgent
                        ? "var(--amber-11)"
                        : "var(--gray-11)",
                    }}
                  />
                  <Text size="2" weight="medium">
                    {comment.isAgent ? "Quack" : "User"}
                  </Text>
                </Flex>

                <Box
                  className={
                    comment.isAgent
                      ? `${styles.messageBubble} ${styles.agentBubble}`
                      : `${styles.messageBubble} ${styles.userBubble}`
                  }
                >
                  <Text size="2">{formatMessage(comment.comment)}</Text>
                </Box>
              </Flex>
            ))}
          </Box>
        )}
      </Flex>

      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <Dialog.Content size="small">
          <Dialog.Title>
            {newFeedback === "FORGOTTEN"
              ? "Forget Conversation"
              : "Mark as Positive"}
          </Dialog.Title>
          <Flex direction="column" gap="4" p="4">
            <Text size="2">
              Are you sure you want to{" "}
              {newFeedback === "FORGOTTEN" ? "forget" : "mark as positive"} this
              conversation?
            </Text>
            <Flex gap="3" justify="end" mt="2">
              <Button
                variant="soft"
                color="gray"
                onClick={handleForgetCancel}
                disabled={isUpdating}
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color={newFeedback === "FORGOTTEN" ? "red" : "green"}
                onClick={handleForgetConfirm}
                disabled={isUpdating}
              >
                {newFeedback === "FORGOTTEN" ? "Forget" : "Mark Positive"}
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog>
    </Box>
  );
}
